import { useDisclosure } from '@mantine/hooks';
import { runInAction } from 'mobx';
import { observer, useLocalObservable } from 'mobx-react-lite';
import type { JSX } from 'react';
import React from 'react';
import { useTranslation } from 'react-i18next';

import type { Importer } from '@feathr/blackbox';
import { Table, Toolbar } from '@feathr/components';
import Page from '@feathr/extender/App/Page';
import { useStore } from '@feathr/extender/state';

import AddImporterButton from './AddImporterButton';
import dataImportsColumns from './dataImportsColumns';
import FilterImportsButton from './FilterImportsButton';
import type { IImportsFilters } from './ImportsFilters';
import ImportsFiltersDrawer from './ImportsFiltersDrawer';

import styles from './DataImportsPage.css';

function DataImportsPage(): JSX.Element {
  const { Importers } = useStore();
  const { t } = useTranslation();

  const [isFiltersDrawerOpen, { open: openFiltersDrawer, close: closeFiltersDrawer }] =
    useDisclosure(false);

  const state = useLocalObservable(() => ({
    filters: {} as IImportsFilters,
    appliedFilters: {} as IImportsFilters,
  }));

  function onApplyFilters(newFilters: IImportsFilters): void {
    runInAction(() => {
      state.filters = newFilters;
      state.appliedFilters = { ...newFilters };
    });
  }

  // Build date filters based on operator
  const getDateFilters = (): Record<string, string> => {
    const operator = state.appliedFilters?.date_operator || 'is';
    const dateFilters: Record<string, string> = {};

    switch (operator) {
      case 'is':
        if (state.appliedFilters?.date_created) {
          // Extract date part and create range to match backend format (microseconds, no timezone)
          const dateOnly = state.appliedFilters.date_created.split('T')[0];
          dateFilters.date_created__gte = `${dateOnly}T00:00:00.000000`;
          dateFilters.date_created__lte = `${dateOnly}T23:59:59.999999`;
        }
        break;

      case 'is_before':
        if (state.appliedFilters?.date_created) {
          const dateOnly = state.appliedFilters.date_created.split('T')[0];
          dateFilters.date_created__lt = `${dateOnly}T00:00:00.000000`;
        }
        break;

      case 'is_after':
        if (state.appliedFilters?.date_created) {
          const dateOnly = state.appliedFilters.date_created.split('T')[0];
          dateFilters.date_created__gt = `${dateOnly}T23:59:59.999999`;
        }
        break;

      case 'is_between':
        if (state.appliedFilters?.date_created__gte) {
          const startDateOnly = state.appliedFilters.date_created__gte.split('T')[0];
          dateFilters.date_created__gte = `${startDateOnly}T00:00:00.000000`;
        }
        if (state.appliedFilters?.date_created__lte) {
          const endDateOnly = state.appliedFilters.date_created__lte.split('T')[0];
          dateFilters.date_created__lte = `${endDateOnly}T23:59:59.999999`;
        }
        break;
    }

    return dateFilters;
  };

  // Build filters for the Table component - only use applied filters
  const baseFilters = {
    ...(state.appliedFilters?.state__in?.length
      ? { state__in: state.appliedFilters.state__in }
      : {}),
    ...getDateFilters(),
    // Convert isOptOut to backend-compatible overrides filter
    ...(state.appliedFilters?.isOptOut === true ? { overrides__not__size: 0 } : {}),
    ...(state.appliedFilters?.isOptOut === false ? { overrides__size: 0 } : {}),
  };

  const tableFilters = {
    kind: 'person',
    ...baseFilters,
  };

  const importers = Importers.list({
    filters: tableFilters,
  });

  const actions = (
    <Toolbar>
      <AddImporterButton />
    </Toolbar>
  );

  return (
    <Page
      actions={actions}
      collapseHeader={false}
      collapseHeaderOnScroll={true}
      description={t(
        'Start new contact data imports or manage existing ones. This is also where you can import lists of unsubscribed contacts.',
      )}
      title={t('Imports')}
    >
      <Toolbar align={'right'} className={styles.filtersToolbar}>
        <FilterImportsButton onClick={openFiltersDrawer} />
      </Toolbar>
      <Table<Importer>
        collection={Importers}
        columns={dataImportsColumns([], t)}
        filters={tableFilters}
        initialSort={[{ id: 'id', desc: true }]}
        isLoading={importers.isPending}
        noDataText={'No imports found.'}
      />
      <ImportsFiltersDrawer
        close={closeFiltersDrawer}
        filters={state.filters}
        isOpen={isFiltersDrawerOpen}
        onApply={onApplyFilters}
      />
    </Page>
  );
}

export default observer(DataImportsPage);
